import { AbilityBuilder, createMongoAbility, MongoAbility } from '@casl/ability';
import { Action } from '../enums/action';
import { IUser } from '../store/models/user';
import { OWNER_ROLE, ROLE_ACCESS, ROLES } from '../constants/roles';
import { ACTION_BY_PAGE, PAGE_TITLE, PAGE_TITLE_COMPONENT } from '../constants/common';
import { Member } from '../store/models/members';

// Define the ability type for strong typing
export type AppAbility = MongoAbility<[string, string]>;

// Function to define the ability
const defineAbility = (currentUser: IUser | null, editingMember: Member | null = null): AppAbility => {
  const { can, cannot, build } = new AbilityBuilder<AppAbility>(createMongoAbility);

  can(Action.Read, PAGE_TITLE.FORBIDDEN_RESOURCE);
  can(Action.Manage, PAGE_TITLE.AUTH_SIGN_IN);
  can(Action.Manage, PAGE_TITLE.NOT_FOUND);
  can(Action.Manage, PAGE_TITLE.AUTH_FORGOT_PASSWORD);

  if (currentUser && !currentUser?.memberData?.active) {
    cannot(Action.Manage, 'all');
    can(Action.Read, PAGE_TITLE.FORBIDDEN_RESOURCE);
    can(Action.Manage, PAGE_TITLE.AUTH_SIGN_IN);
    can(Action.Manage, PAGE_TITLE.NOT_FOUND);
    can(Action.Manage, PAGE_TITLE.AUTH_FORGOT_PASSWORD);
    can(Action.Manage, PAGE_TITLE.PROFILE);
  }

  if (currentUser && currentUser?.memberData?.active) {
    switch (currentUser.memberData.role) {
    case OWNER_ROLE: {
      can(Action.Manage, 'all');

      if (editingMember && editingMember.role === OWNER_ROLE) {
        cannot(Action.Update, ACTION_BY_PAGE[PAGE_TITLE.EDIT_MEMBER].CHANGE_LOCATIONS);
      }
      break;
    };
    case ROLES.SUPERVISOR: {
      can(Action.Manage, 'all');
      cannot(Action.Manage, PAGE_TITLE.ORGANIZATIONS);
      cannot(Action.Update, ACTION_BY_PAGE[PAGE_TITLE.EDIT_CONTRACT].SAVE_AS_CLIENT_DEFAULT);

      if (editingMember && editingMember.role === OWNER_ROLE) {
        cannot(Action.Update, ACTION_BY_PAGE[PAGE_TITLE.EDIT_MEMBER].CHANGE_LOCATIONS);
        cannot(Action.Update, ACTION_BY_PAGE[PAGE_TITLE.EDIT_MEMBER].CHANGE_ROLE);
      }
      break;
    };
    case ROLES.OPERATOR: {
      cannot(Action.Delete, PAGE_TITLE.CREATE_SCREEN);

      can(Action.Manage, PAGE_TITLE.CYCLE_ATTACHMENTS);

      can(Action.Manage, PAGE_TITLE.PROFILE);

      can(Action.Manage, PAGE_TITLE_COMPONENT[PAGE_TITLE.CYCLE].NOTE);
      can(Action.Manage, PAGE_TITLE_COMPONENT[PAGE_TITLE.CYCLE].CHART_PREFERENCES);

      // Complete access to all controller-related pages
      can(Action.Manage, PAGE_TITLE.CONTROLLERS);
      can(Action.Manage, PAGE_TITLE.CONTROLLER_CHANNELS);
      can(Action.Manage, PAGE_TITLE.CONTROLLER_CHANNEL_FAULTS);
      can(Action.Manage, PAGE_TITLE.CONTROLLER_CHANNELS_NAMES);
      can(Action.Manage, PAGE_TITLE.CONTROLLER_CHANNELS_COLORS);
      can(Action.Manage, PAGE_TITLE.EDIT_CONTROLLER);
      can(Action.Manage, PAGE_TITLE.EDIT_CONTROLLER_SYSTEM_FAULTS);
      can(Action.Manage, PAGE_TITLE.EDIT_CONTROLLER_CHANNEL);
      can(Action.Manage, PAGE_TITLE.EDIT_CONTROLLER_SETTINGS);
      can(Action.Manage, PAGE_TITLE.EDIT_CONTROLLER_FAULT_CONTROL);
      can(Action.Manage, PAGE_TITLE.EDIT_CONTROLLER_POWER_SHARE);
      //

      can(Action.Manage, PAGE_TITLE.CYCLE_REPORT);

      // Page with cycle relation
      can(Action.Create, PAGE_TITLE.ADD_CYCLE);
      can(Action.Read, PAGE_TITLE.ADD_CYCLE);
      can(Action.Read, PAGE_TITLE.CYCLE);
      can(Action.Read, PAGE_TITLE.EDIT_CYCLE);
      can(Action.Update, PAGE_TITLE.CYCLE);
      can(Action.Update, PAGE_TITLE.EDIT_CYCLE);
      //

      can(Action.Read, PAGE_TITLE.CREATE_SCREEN);
      can(Action.Read, PAGE_TITLE.OVERVIEW);
      can(Action.Update, PAGE_TITLE.EDIT_SCREEN);
      can(Action.Read, PAGE_TITLE.HOME);
      can(Action.Read, PAGE_TITLE.LOCATION_CONTRACTS);
      can(Action.Read, PAGE_TITLE.LOCATION_CONTRACT_SCREENS);
      can(Action.Read, PAGE_TITLE.SCREEN_CYCLES);
      can(Action.Manage, PAGE_TITLE.CHANNEL_QUICK_SET);

      break;
    };
    case ROLES.CUSTOMER: {
      cannot(Action.Delete, PAGE_TITLE_COMPONENT[PAGE_TITLE.CYCLE].NOTE);
      cannot(Action.Create, PAGE_TITLE_COMPONENT[PAGE_TITLE.CYCLE].NOTE);
      cannot(Action.Update, PAGE_TITLE_COMPONENT[PAGE_TITLE.CYCLE].CHART_PREFERENCES);
      cannot(Action.Manage, PAGE_TITLE.OVERVIEW);

      can(Action.Manage, PAGE_TITLE.PROFILE);

      can(Action.Read, PAGE_TITLE.HOME);
      can(Action.Read, PAGE_TITLE.CYCLE);
      can(Action.Read, PAGE_TITLE.LOCATIONS);
      can(Action.Read, PAGE_TITLE.LOCATION_CONTRACTS);
      can(Action.Read, PAGE_TITLE.LOCATION_CONTRACT_SCREENS);
      can(Action.Read, PAGE_TITLE.SCREEN_CYCLES);

      can(Action.Update, PAGE_TITLE_COMPONENT[PAGE_TITLE.CYCLE].NOTE);

      cannot(Action.Read, PAGE_TITLE_COMPONENT[PAGE_TITLE.CYCLE].CHART_PREFERENCES);
      break;
    };
    }

    if (editingMember) {
      const isHigherLevelThanTarget = ROLE_ACCESS[currentUser.memberData.role] > ROLE_ACCESS[editingMember.role];
      // Operators cannot activate/deactivate any member, regardless of hierarchy.
      const canChangeActiveStatus = isHigherLevelThanTarget && currentUser.memberData.role !== ROLES.OPERATOR;

      (isHigherLevelThanTarget ? can : cannot)(Action.Update, ACTION_BY_PAGE[PAGE_TITLE.EDIT_MEMBER].CHANGE_PASSWORD);
      (canChangeActiveStatus ? can : cannot)(Action.Update, ACTION_BY_PAGE[PAGE_TITLE.EDIT_MEMBER].CHANGE_ACTIVE_STATUS);
    }
  }

  return build();
};

export default defineAbility;
